type Boundary = {
  top: number
  left: number
  bottom: number
  right: number
}

/**
 * Returns a Boundary object from getBoundingClientRect
 */
export const getBoundary = (element: HTMLElement): Boundary => {
  const { top, left, bottom, right } = element.getBoundingClientRect()

  return {
    top,
    left,
    bottom,
    right,
  }
}

/**
 * Takes the trigger and popover Boundaries and modifies the popover Boundary to meet the trigger.
 * Used to cover the Popover offset (e.g. arrow).
 */
export const getInteractiveBoundaries = (
  triggerRect: Boundary,
  popoverRect: Boundary,
): Boundary[] => {
  const extendedPopover = { ...popoverRect }

  // popover is above trigger, extend down to triggerRect
  if (popoverRect.bottom < triggerRect.top) {
    extendedPopover.bottom = triggerRect.top
  }

  // popover is below trigger, extend up to triggerRect
  if (popoverRect.top > triggerRect.bottom) {
    extendedPopover.top = triggerRect.bottom
  }

  // popover is after trigger, extend left to triggerRect
  if (popoverRect.left > triggerRect.right) {
    extendedPopover.left = triggerRect.right
  }

  // popover is before trigger, extend right to triggerRect
  if (popoverRect.right < triggerRect.left) {
    extendedPopover.right = triggerRect.left
  }

  return [triggerRect, extendedPopover]
}

/**
 * Checks if the current mouse position is within an array of provided Boundaries.
 */
export const isWithinInteractiveBoundary = (
  mouseEvent: React.MouseEvent,
  boundaryRects: Boundary[],
  offset = -2,
): boolean => {
  const { clientX, clientY } = mouseEvent
  // Find the first rect that the mouse cursor is in
  return !!boundaryRects.find((rect) => {
    const exceedsLeft = clientX < Math.floor(rect.left - offset)
    const exceedsRight = clientX > Math.ceil(rect.right + offset)
    const exceedsTop = clientY < Math.floor(rect.top - offset)
    const exceedsBottom = clientY > Math.ceil(rect.bottom + offset)

    return !exceedsBottom && !exceedsLeft && !exceedsRight && !exceedsTop
  })
}
